
import {defineComponent, ref, watch} from "vue";
import {
  CodeDescriptionRestDto,
  DiscountSchemeRestDto,
  FullMultilingualProductLabelRestDto,
  MultilingualFieldRestDto,
  MultilingualProductLabelRestDto,
} from "@/api/pharma-cpc-mgmt/models";
import {defineRule} from "vee-validate";
import CatalogBuildStatus from "@/components/layouts/catalog/CatalogBuildStatus.vue";
import DiscountPricelistSection from "@/components/layouts/catalog/discounts-pricelists/DiscountPriceListSection.vue";
import ProductFilter from "@/components/layouts/catalog/product-filter/ProductFilter.vue";
import {UtilityRestService} from "@/services/rest/cms-utility/UtilityRestService";
import MultilingualSwitcher from "@/components/UI/MultilingualSwitcher.vue";
import {I18nContext} from "@/context/I18nContext";
import {useToast} from "vue-toastification";
import BaseTitle from "@/components/UI/BaseTitle.vue";
import I18nInputTextEditor from "@/components/UI/I18n/I18nInputTextEditor.vue";
import {MultilingualRestDtoModel} from "@/models/MultilingualRestDtoModel";
import {isEmptyString} from "@/helpers/functions/string";
import ButtonProductFilterPreview from "@/components/layouts/catalog/ButtonProductFilterPreview.vue";
import {DiscountTypeEnum} from "@/api/enums/discount-type-enum";
import {StatusEnum} from "@/api/enums/status-enum";
import {DiscountSchemeTypeEnum} from "@/api/enums/discount-scheme-type-enum";
import {EnumClassNameCpcEnum} from "@/models/enum/EnumClassNameCpcEnum";
import {DiscountSchemeRestService} from "@/services/rest/cpc-mgmt/DiscountSchemeRestService";
import {UIStateDto} from "@/dtos/UIStateDto";
import StaffDiscountsteps from "@/components/layouts/catalog/discount-steps/StaffDiscountSteps.vue";
import BaseOffcanvasButtonOpen from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonOpen.vue";
import BaseOffcanvas from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvas.vue";
import ProductLabel from "@/components/layouts/catalog/product-labels/ProductLabel.vue";
import {AbilityContext} from "@/context/AbilityContext";
import RenderedProductLabel from "@/components/layouts/catalog/product-labels/RenderedProductLabel.vue";
import {ProductLabelRestService} from "@/services/rest/cpc-mgmt/ProductLabelRestService";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import DefaultLabel from "@/components/layouts/catalog/product-labels/DefaultLabel.vue";
import {ColorSchemeRestDtoModel} from "@/models/api/pharma-cms-color-scheme/ColorSchemeRestDtoModel";
import {ColorSchemeRestService} from "@/services/rest/cms-color-scheme/ColorSchemeRestService";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import {FormMixin} from "@/components/base/FormMixin";
import GenericFormWrapper from "@/components/base/GenericFormWrapper.vue";
import {handleSavedSuccessfully} from "@/helpers/toast-helper";
import {DiscountSchemeRestDtoModel} from "@/models/api/pharma-cpc-mgmt/DiscountSchemeRestDtoModel";
import {ClientGroupManagementService} from "@/services/rest/idm-user/ClientGroupManagementService";
import {formatDateObjectToDateString} from "@/helpers/functions/date-time";

//TODO multilingual
defineRule("check_date", (value: any, [begin, end]: any) => {
  if ((begin && end) && begin > end) {
    return 'De einddatum moet gelijk aan of later zijn';
  }
  return true;
});

export default defineComponent({
  name: "DiscountsDetail",
  mixins: [FormMixin],
  components: {
    GenericFormWrapper,
    DefaultLabel,
    RenderedProductLabel,
    ProductLabel,
    StaffDiscountsteps,
    ButtonProductFilterPreview,
    I18nInputTextEditor,
    BaseTitle,
    BaseOffcanvasButtonOpen,
    BaseOffcanvas,
    MultilingualSwitcher,
    ProductFilter,
    DiscountPricelistSection,
    CatalogBuildStatus
  },
  props: ['mode'],
  data() {
    return {
      id: this.$route.params.id,
      canShowDiscountMessage: true,
      returnRoute: 'discounts-price-lists' as string,

      discountScheme: DiscountSchemeRestDtoModel.createWithDefaults() as DiscountSchemeRestDto,
      discountMessageTimeoutId: null as number | null,
      discountMessage: MultilingualRestDtoModel.createWithDefaults(),

      activeLanguage: I18nContext.getDefaultApiLanguageKey(),
      //Hack: and errors object has to be passed to the MultilingualSwitcher, but I can't use the errors object from the form,
      // because this switcher is not inside the form.
      languageSwitchErrors: ref([]),

      discountTypeOptions: [] as CodeDescriptionRestDto[],
      combinationDiscountOptions: [] as CodeDescriptionRestDto[],
      clientGroups: [] as CodeDescriptionRestDto[],

      staffKey: 0,
      toaster: useToast(),
      pageUI: UIStateDto.createWithDefaults(),
      productLabel: null as MultilingualProductLabelRestDto | null,
      colorScheme: ColorSchemeRestDtoModel.createWithDefaults(),
    }
  },
  mounted() {
    this.initialize();

    //This hack is needed to make sure the tinyMCE editor for the discount message does not crash when
    //the editor for the unapplied message is removed.
    //TinyMCE removes something global in the unmount hook.  So if a page contains more than 1 TinyMCE editor, and one of them
    //is removed, then the others no longer function.  So we remove the other one here, wait for the next tick, and add it again.
    //When adding a new one, proper initialization is done, and the editor works fine.
    watch(
      () => this.isShowUnappliedMessage, (v) => {
        if (!v) {
          this.canShowDiscountMessage = false;
          this.$nextTick(() => {
            this.canShowDiscountMessage = true;
          });
        }
      }
    );
  },
  computed: {
    PageDetailModeEnum() {
      return PageDetailModeEnum
    },
    AbilityContext() {
      return AbilityContext
    },
    DiscountSchemeTypeEnum() {
      return DiscountSchemeTypeEnum
    },
    isDiscountValueApplicable(): boolean {
      return this.discountScheme.scheme_type_code !== undefined && this.discountScheme.scheme_type_code === DiscountSchemeTypeEnum.MANUAL_INPUT
        && (this.discountScheme.type_code !== DiscountTypeEnum.BUY_X_GET_Y_FREE && this.discountScheme.type_code !== DiscountTypeEnum.PERCENTAGE_PER_QUANTITY && this.discountScheme.type_code !== DiscountTypeEnum.AMOUNT_PER_QUANTITY);
    },
    isStaffDiscount(): boolean {
      return this.discountScheme.scheme_type_code !== undefined && this.discountScheme.scheme_type_code === DiscountSchemeTypeEnum.MANUAL_INPUT
        && (this.discountScheme.type_code === DiscountTypeEnum.AMOUNT_PER_QUANTITY || this.discountScheme.type_code === DiscountTypeEnum.PERCENTAGE_PER_QUANTITY);
    },
    isXYDiscount(): boolean {
      return this.discountScheme.scheme_type_code !== undefined && this.discountScheme.scheme_type_code === DiscountSchemeTypeEnum.MANUAL_INPUT
        && (this.discountScheme.type_code === DiscountTypeEnum.BUY_X_GET_Y_FREE || this.discountScheme.type_code === DiscountTypeEnum.BUY_X_GET_Y_PERCENTAGE || this.discountScheme.type_code === DiscountTypeEnum.BUY_X_GET_Y_AMOUNT);
    },
    isOrderBasedDiscount(): boolean {
      return this.discountScheme.scheme_type_code !== undefined && this.discountScheme.scheme_type_code === DiscountSchemeTypeEnum.MANUAL_INPUT
        && (this.discountScheme.type_code !== DiscountTypeEnum.PERCENTAGE && this.discountScheme.type_code !== DiscountTypeEnum.AMOUNT);
    },
    isShowUnappliedMessage(): boolean {
      return this.discountScheme.type_code === DiscountTypeEnum.PERCENTAGE_PER_QUANTITY
        || this.discountScheme.type_code === DiscountTypeEnum.BUY_X_GET_Y_FREE
        || this.discountScheme.type_code === DiscountTypeEnum.AMOUNT_PER_QUANTITY
        || this.discountScheme.type_code === DiscountTypeEnum.BUY_X_GET_Y_AMOUNT
        || this.discountScheme.type_code === DiscountTypeEnum.BUY_X_GET_Y_PERCENTAGE;
    },
    discountLabel(): string {
      switch (this.discountScheme.type_code) {
        case DiscountTypeEnum.PERCENTAGE: {
          if (this.discountScheme.discount_value) {
            return `- ${this.discountScheme.discount_value}%`;
          }
          break;
        }
        case DiscountTypeEnum.AMOUNT: {
          if (this.discountScheme.discount_value) {
            return `€ -${this.discountScheme.discount_value}`;
          }
          break;
        }
        case DiscountTypeEnum.BUY_X_GET_Y_FREE: {
          return this.$t(
            'buyXGetYFree',
            {buyQuantity: this.discountScheme.buy_quantity, getQuantity: this.discountScheme.get_quantity}
          );
        }
        case DiscountTypeEnum.BUY_X_GET_Y_PERCENTAGE: {
          return this.$t(
            'buyXGetYPercentage',
            {
              buyQuantity: this.discountScheme.buy_quantity,
              getQuantity: this.discountScheme.get_quantity,
              percentage: this.discountScheme.discount_value
            }
          );
        }
        case DiscountTypeEnum.BUY_X_GET_Y_AMOUNT: {
          return this.$t(
            'buyXGetYDiscount',
            {
              buyQuantity: this.discountScheme.buy_quantity,
              getQuantity: this.discountScheme.get_quantity,
              amount: this.discountScheme.discount_value
            }
          );
        }
        case DiscountTypeEnum.PERCENTAGE_PER_QUANTITY:
        case DiscountTypeEnum.AMOUNT_PER_QUANTITY: {
          return this.$t('discountStaffDiscount');
        }
      }

      return '';
    },
    selectedDiscountOption: {
      get(): string {
        const discountVisible = this.discountScheme.is_discount_visible;
        const standardPriceVisible = this.discountScheme.is_standard_price_visible;

        if (!discountVisible && !standardPriceVisible) {
          return 'none';
        } else if (!discountVisible && standardPriceVisible) {
          return 'price';
        } else if (discountVisible && standardPriceVisible) {
          return 'pricePercent';
        } else {
          return '';
        }
      },
      set(value: string) {
        switch (value) {
          case 'none':
            this.discountScheme.is_standard_price_visible = false;
            this.discountScheme.is_discount_visible = false;
            break;
          case 'price':
            this.discountScheme.is_standard_price_visible = true;
            this.discountScheme.is_discount_visible = false;
            break;
          case 'pricePercent':
            this.discountScheme.is_standard_price_visible = true;
            this.discountScheme.is_discount_visible = true;
            break;
          default:
            throw new Error('Unsupported selectedDiscountOption to set.');
        }
      }
    },
    selectedDiscountOptionImage(): string | null {
      switch (this.selectedDiscountOption) {
        case '':
        case 'none':
          return 'product_card_discount_none.png';
        case 'price':
        case 'pricePercent':
          return 'product_card_discount_price.png';
        // case 'pricePercent':
        //   return 'product_card_discount_price_percentage.png';
        default:
          throw new Error('Unsupported selectedDiscountOption to search.');
      }
    },
    statusOptions(): CodeDescriptionRestDto[] {
      return [
        {
          code: StatusEnum.PUBLISHED,
          description: this.$t('published'),
        },
        {
          code: StatusEnum.NOT_PUBLISHED,
          description: this.$t('notPublished'),
        },
      ];
    },
  },
  methods: {
    isEmptyString,
    async doInitialize(): Promise<void> {
      this.discountTypeOptions =
        await EnumRestService.getInstance().getCpcEnumValuesByClassname(EnumClassNameCpcEnum.DISCOUNT_TYPE);
      this.combinationDiscountOptions =
        await EnumRestService.getInstance().getCpcEnumValuesByClassname(EnumClassNameCpcEnum.COMBINATION_DISOOUNT_SCOPE);
      this.colorScheme = await ColorSchemeRestService.getInstance().findColorScheme();
      const groups = await ClientGroupManagementService.getInstance().findClientGroups();
      this.clientGroups = [{code: '', description: this.$t('none')}, ...groups];
    },
    removeDiscountSchemePeriod(): void {
      this.discountScheme.begin_date = formatDateObjectToDateString(new Date());
      this.discountScheme.end_date = undefined;
      this.onFormDataChanged();
    },
    async doSubmit(): Promise<void> {
      if (this.mode === PageDetailModeEnum.NEW) {
        await DiscountSchemeRestService.getInstance().createDiscountScheme(this.discountScheme);
      } else {
        await DiscountSchemeRestService.getInstance().updateDiscountScheme(this.id, this.discountScheme);
      }
    },
    async doReload(): Promise<void> {
      if (this.mode === PageDetailModeEnum.NEW) {
        this.discountScheme = DiscountSchemeRestDtoModel.createWithDefaults();
      } else {
        this.discountScheme = await DiscountSchemeRestService.getInstance().findDiscountScheme(this.id);
        if (!this.discountScheme.is_scheme_cumulative_with_promo_codes) {
          this.discountScheme.is_scheme_cumulative_with_promo_codes = false;
        }
      }
      this.truncateDiscountMessage();
      await this.doReloadLabel();
    },
    async doReloadLabel(): Promise<void> {
      const labelResult: FullMultilingualProductLabelRestDto | null = this.id
        ? await ProductLabelRestService.getInstance().getProductLabelForDiscountScheme(this.id)
        : null;
      if (labelResult) {
        this.productLabel = labelResult.label;
      } else {
        this.productLabel = null;
      }
    },
    async reloadLabel(): Promise<void> {
      await this.performGenericReload(this.doReloadLabel);
    },
    async afterSave() {
      if (this.mode === PageDetailModeEnum.NEW) {
        await this.$router.push({name: this.returnRoute});
      } else {
        window.scrollTo(0, 0);
        await handleSavedSuccessfully();
        await this.reloadContent();
      }
    },
    discountMessageUpdated(value: any): void {
      this.discountScheme.discount_message = value;
      this.truncateDiscountMessage();
      this.onFormDataChanged();
    },
    truncateDiscountMessage(): void {
      if (this.discountMessageTimeoutId !== null) {
        clearTimeout(this.discountMessageTimeoutId);
      }

      this.discountMessageTimeoutId = setTimeout(() => {
        Object.keys(this.discountMessage).forEach(async (key: string) => {
          if (this.discountScheme.discount_message !== null && this.discountScheme.discount_message !== undefined) {
            let value = this.discountScheme.discount_message[key as keyof MultilingualFieldRestDto];
            if (!isEmptyString(value)) {
              value = await UtilityRestService.getInstance()
                .truncateHtml(value, 100);
            }

            this.discountMessage.setPropValue(key, value);
          }
        });
      }, 1000);
    },
    staffStepsUpdate(): void {
      this.staffKey++;
    },
  },
});
